import { forwardRef } from 'react';

import { afterPseudoClasses, backgroundColorClasses, baseClasses, beforePseudoClasses, linkTextClasses } from './PromoBanner.classes';
import type { GlobalBannerData, PromoBannerProps } from './PromoBanner.types';

import { cn } from 'helpers/classnames';
import InfoOutlineIcon from 'components/common/IconsV2/InfoOutlineIcon';
import MartyLink from 'components/common/MartyLink';
import useMartyContext from 'hooks/useMartyContext';
import { HtmlToReact } from 'components/common/HtmlToReact';

export function getFirstBanner(globalBanner?: GlobalBannerData) {
  if (globalBanner?.data?.slotNames?.length) {
    const firstSlot = globalBanner.data.slotNames[0];
    const firstBanner = globalBanner.data.slotData[firstSlot as keyof typeof globalBanner.data.slotData];
    return firstBanner;
  }

  return undefined;
}

export const PromoBanner = forwardRef<HTMLDivElement, PromoBannerProps>((props: PromoBannerProps, ref) => {
  const { globalBanner, className, ...forwardProps } = props;
  const { testId: getTestId } = useMartyContext();

  const firstBanner = getFirstBanner(globalBanner);

  if (!firstBanner) {
    return null;
  }

  const { backgroundColor = 'primary', color, linktext, href, content, isInfoIcon = false } = firstBanner;

  const isPredefinedBackgroundColor = ['primary', 'secondary', 'tertiary'].includes(backgroundColor);
  const backgroundColorClass = isPredefinedBackgroundColor ? backgroundColorClasses[backgroundColor] : '';
  const backgroundColorStyle = !isPredefinedBackgroundColor ? backgroundColor : undefined;

  const styleContainer = {
    backgroundColor: backgroundColorStyle as string | undefined,
    color
  };

  return (
    <div
      className={cn(baseClasses, backgroundColorClass, beforePseudoClasses, afterPseudoClasses, className)}
      style={styleContainer}
      {...forwardProps}
      ref={ref}
      data-test-id={getTestId('promoBanner')}
    >
      {isInfoIcon && (
        <div className="mr-2 inline-block">
          <InfoOutlineIcon data-test-id={getTestId('infoIcon')} size={24} />
        </div>
      )}
      <div className="text-balance text-center">
        <HtmlToReact noContainer={true}>{content}</HtmlToReact>
        {href && linktext && (
          <MartyLink to={href} className={linkTextClasses} style={styleContainer}>
            {linktext}
          </MartyLink>
        )}
      </div>
    </div>
  );
});

PromoBanner.displayName = 'PromoBanner';
